import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ContactGroup } from '@app/core/interfaces/c5/contact-group';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { ContactGroupFormModel } from '@app/shared/components/update-contact-group/contact-group-form-model';
import { UpdateContactGroupInput } from '@app/shared/components/update-contact-group/update-contact-group-input';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import { toMap } from '@app/utils/common';
import { PortalError } from '@app/utils/error/portal-error';
import { isBlank } from '@app/utils/string';
import { StringMap } from '@app/utils/string-map';
import { FormGroupTyped } from 'src/types/typed-forms';
import { ContactsMode } from '../contacts/contacts.mode';

@Component({
  selector: 'portal-update-contact-group',
  templateUrl: './update-contact-group.component.html',
  standalone: false,
})
export class UpdateContactGroupComponent
  extends DialogComponent<UpdateContactGroupInput, void>
  implements OnChanges, OnInit, UpdateContactGroupInput
{
  contactGroup?: ContactGroup;
  contacts: PhoneBookContact[];
  mode!: ContactsMode;
  update: (contactGroup: ContactGroup | Partial<ContactGroup>) => Promise<any>;

  contactsMode = ContactsMode;
  error?: PortalError;
  entry: ContactGroup | Partial<ContactGroup>;
  form: FormGroupTyped<ContactGroupFormModel> = ContactGroupFormModel.formGroup();

  isFavorite: boolean;
  isNew: boolean;

  canAddOrDelete: boolean = false;

  // Save the current value so we can reset to this value
  resetValue: ContactGroupFormModel;
  saving: boolean = false;
  title: string;

  private contactMap: StringMap<PhoneBookContact> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if ('contacts' in changes && changes.contacts.firstChange === false) {
      this.contactMap = toMap(changes.contacts.currentValue, 'id');
    }

    if ('contactGroup' in changes && changes.contactGroup.firstChange === false) {
      this.resetValue = {
        ...ContactGroupFormModel.from(changes.contactGroup.currentValue),
        contacts: this.calculateContacts(changes.contactGroup.currentValue),
      };

      this.form.patchValue(this.resetValue);
    }
  }

  ngOnInit() {
    if (this.mode !== ContactsMode.CALL_CENTER_AGENT) {
      this.canAddOrDelete = true;
    }

    if (this.contactGroup !== undefined) {
      this.entry = this.contactGroup;
      this.isFavorite = this.contactGroup.name === '';
      this.isNew = false;
    } else {
      this.entry = this.newEntry();
      this.isFavorite = false;
      this.isNew = true;
    }

    this.contactMap = toMap(this.contacts, 'id');

    this.resetValue = {
      ...ContactGroupFormModel.from(this.entry),
      contacts: this.calculateContacts(this.entry),
    };

    this.form.patchValue(this.resetValue);

    if (this.isNew === false && isBlank(this.entry.name)) {
      this.form.controls.name.setValidators([]);
    }
  }

  reset() {
    this.form.reset(this.resetValue);
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  trigger(submit = false) {
    if (submit) {
      this.resetValue = this.form.getRawValue();

      const model = ContactGroupFormModel.merge(this.entry, this.resetValue);

      this.saving = true;

      this.update(model)
        .then(() => {
          this.close();
        })
        .catch((error) => {
          this.error = error;
        })
        .finally(() => {
          this.saving = false;
        });
    } else {
      this.dismiss();
    }
  }

  private calculateContacts(contactGroup: Partial<ContactGroup>) {
    return (contactGroup.entries ?? []).map((entry) => {
      return this.contactMap[entry.abId];
    });
  }

  private newEntry(): Partial<ContactGroup> {
    return {
      entries: [],
      id: 0,
    };
  }
}
